import React, { useCallback, useEffect, useState } from 'react';
import { api, ApiError, downloadFile, mutate } from '../shared/api';
import { formatDate, formatMoney, formatNumber } from '../shared/formatting';

type Locale = 'hr' | 'en';

type State = 'zaprimljeno' | 'poslano_tca' | 'analizirano' | 'fakturirano';

type Note = {
    id: number;
    office: string;
    note_date: string | null;
    note_no: string | null;
    sent_date: string | null;
    arrived_date: string | null;
    gross_g: number | null;
    fine_au_g: number | null;
    batch_id: number | null;
    inbound_shipment_id: number | null;
    note: string | null;
    source: string;
    state: State;
};

type Invoice = {
    id: number;
    batch_id: number | null;
    delivery_note_id: number | null;
    invoice_date: string | null;
    invoice_no: string | null;
    amount_eur: number;
    direction: 'ulazni' | 'izlazni';
    note: string | null;
    price_per_g: number | null;
    price_basis: 'otpremnica' | 'tca' | 'ad' | null;
    price_dev_pct: number | null;
    price_outlier: boolean;
};

type Batch = {
    id: number;
    sent_g: number | null;
    ad_doc_no: string | null;
    ad_doc_date: string | null;
    lom_tca_calculation_id: number | null;
    calc: { iznos_eur: number; booking_status: string; datum_dokumenta: string; partner_naziv: string } | null;
    ad_fine_g: number | null;
    export_date: string | null;
    tca_fine_g: number | null;
    note: string | null;
    state: State;
    notes_gross_g: number;
    notes_fine_g: number;
    sent_diff_g: number | null;
    sent_diff_flag: boolean;
    deviation_pct: number | null;
    deviation_flag: boolean;
    invoices_eur: number;
    price_per_g: number | null;
    notes: Note[];
    invoices: Invoice[];
};

type Overview = {
    settings: { deviation_threshold_pct: number; price_outlier_pct: number };
    batches: Batch[];
    unbatched_notes: Note[];
    invoices_without_batch: Invoice[];
    median_price_per_g: number | null;
    totals: { notes: number; batches: number; gross_g: number; fine_au_g: number; ad_fine_g: number; tca_fine_g: number; deviation_pct: number | null; invoices_eur: number };
};

type Exception = { type: string; label: string; ref: string | null; date: string | null; amount_eur?: number; batch_id?: number; invoice_id?: number; note_id?: number };

type FieldDef = { key: string; hr: string; en: string; kind: 'text' | 'date' | 'num' | 'select'; options?: { value: string; label: string }[] };

type Editing = { entity: 'notes' | 'batches' | 'invoices'; id: number | null; values: Record<string, string>; noteIds?: number[] };

const STATE_LABEL: Record<State, { hr: string; en: string }> = {
    zaprimljeno: { hr: 'Zaprimljeno', en: 'Received' },
    poslano_tca: { hr: 'Poslano TCA', en: 'Sent to TCA' },
    analizirano: { hr: 'Analizirano', en: 'Analysed' },
    fakturirano: { hr: 'Fakturirano', en: 'Invoiced' },
};

const BOOKING_LABEL: Record<string, string> = { knjizeno: 'knjiženo', storno: 'storno', neproknjizeno: 'neproknjiženo' };

const NOTE_FIELDS: FieldDef[] = [
    { key: 'office', hr: 'Ured', en: 'Office', kind: 'select', options: [{ value: 'PLOVDIV', label: 'Plovdiv' }, { value: 'SOFIJA', label: 'Sofija' }, { value: 'OSTALO', label: 'Ostalo' }] },
    { key: 'note_no', hr: 'Broj otpremnice', en: 'Delivery note no.', kind: 'text' },
    { key: 'note_date', hr: 'Datum otpremnice', en: 'Delivery note date', kind: 'date' },
    { key: 'sent_date', hr: 'Datum slanja', en: 'Sent', kind: 'date' },
    { key: 'arrived_date', hr: 'Datum dolaska', en: 'Arrived', kind: 'date' },
    { key: 'gross_g', hr: 'Bruto g', en: 'Gross g', kind: 'num' },
    { key: 'fine_au_g', hr: 'Fine Au g', en: 'Fine Au g', kind: 'num' },
    { key: 'note', hr: 'Napomena', en: 'Note', kind: 'text' },
];

const BATCH_FIELDS: FieldDef[] = [
    { key: 'ad_doc_no', hr: 'Broj dokumenta AD', en: 'AD document no.', kind: 'text' },
    { key: 'ad_doc_date', hr: 'Datum dokumenta AD', en: 'AD document date', kind: 'date' },
    { key: 'sent_g', hr: 'Masa poslana TCA g', en: 'Sent to TCA g', kind: 'num' },
    { key: 'ad_fine_g', hr: 'Fine po dokumentu AD g', en: 'AD fine g', kind: 'num' },
    { key: 'export_date', hr: 'Datum izvoza', en: 'Export date', kind: 'date' },
    { key: 'tca_fine_g', hr: 'Fine prema TCA g', en: 'TCA fine g', kind: 'num' },
    { key: 'note', hr: 'Napomena', en: 'Note', kind: 'text' },
];

const INVOICE_FIELDS: FieldDef[] = [
    { key: 'invoice_no', hr: 'Broj računa', en: 'Invoice no.', kind: 'text' },
    { key: 'invoice_date', hr: 'Datum računa', en: 'Invoice date', kind: 'date' },
    { key: 'amount_eur', hr: 'Iznos EUR', en: 'Amount EUR', kind: 'num' },
    { key: 'direction', hr: 'Smjer', en: 'Direction', kind: 'select', options: [{ value: 'ulazni', label: 'Ulazni (GTM nama)' }, { value: 'izlazni', label: 'Izlazni (mi GTM-u)' }] },
    { key: 'note', hr: 'Napomena', en: 'Note', kind: 'text' },
];

const toNum = (v: string): number | null => {
    const s = v.trim().replace(/\s/g, '');
    if (!s) return null;
    const n = Number(s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s);
    return Number.isFinite(n) ? n : null;
};

const toValues = (fields: FieldDef[], src: Record<string, unknown>): Record<string, string> => {
    const out: Record<string, string> = {};
    fields.forEach((f) => {
        const v = src[f.key];
        out[f.key] = v === null || v === undefined ? '' : String(v);
    });
    return out;
};

/** Modul 40.47 Lom GTM: otpremnice ureda u Plovdivu i Sofiji, pošiljke prema TCA s odstupanjem fine mase i računi. */
export function GtmScrapModule({ locale, token, onBack }: { locale: Locale; token: string | null; onBack?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const g = (v: number | null | undefined) => (v === null || v === undefined ? '' : formatNumber(v, locale));
    const money = (v: number | null | undefined) => (v === null || v === undefined ? '' : formatMoney(v, locale));
    const pct = (v: number | null | undefined) => (v === null || v === undefined ? '' : `${formatNumber(Math.round(v * 100) / 100, locale)} %`);
    const d = (v: string | null) => (v ? formatDate(v, locale) : '');

    const [filters, setFilters] = useState({ office: '', from: '', to: '', state: '' });
    const [data, setData] = useState<Overview | null>(null);
    const [exceptions, setExceptions] = useState<Exception[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editing, setEditing] = useState<Editing | null>(null);
    const [busy, setBusy] = useState(false);
    const [settingsForm, setSettingsForm] = useState<{ deviation_threshold_pct: string; price_outlier_pct: string } | null>(null);

    const query = () => {
        const p = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => {
            if (v) p.set(k, v);
        });
        const s = p.toString();
        return s ? `?${s}` : '';
    };

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        Promise.all([api<Overview>(`/gtm-scrap/notes${query()}`, token), api<{ rows: Exception[] }>('/gtm-scrap/exceptions', token)])
            .then(([o, e]) => {
                setData(o);
                setExceptions(e.rows);
            })
            .catch((e) => setError(e instanceof ApiError ? e.message : t('Učitavanje nije uspjelo.', 'Loading failed.')))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, filters]);

    useEffect(() => {
        load();
    }, [load]);

    const fieldsFor = (e: Editing['entity']) => (e === 'notes' ? NOTE_FIELDS : e === 'batches' ? BATCH_FIELDS : INVOICE_FIELDS);

    const save = async () => {
        if (!editing) return;
        setBusy(true);
        setError(null);
        const body: Record<string, unknown> = {};
        fieldsFor(editing.entity).forEach((f) => {
            const v = editing.values[f.key] ?? '';
            body[f.key] = f.kind === 'num' ? toNum(v) : v.trim() === '' ? null : v.trim();
        });
        if (editing.entity === 'batches' && editing.noteIds) body.note_ids = editing.noteIds;
        if (editing.entity === 'invoices') {
            body.batch_id = editing.values.batch_id ? Number(editing.values.batch_id) : null;
            body.delivery_note_id = editing.values.delivery_note_id ? Number(editing.values.delivery_note_id) : null;
            if (!body.direction) body.direction = 'ulazni';
        }
        if (editing.entity === 'notes' && !body.office) body.office = 'OSTALO';
        try {
            if (editing.id === null) await mutate(`/gtm-scrap/${editing.entity}`, token, 'POST', body);
            else await mutate(`/gtm-scrap/${editing.entity}/${editing.id}`, token, 'PATCH', body);
            setEditing(null);
            load();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : t('Spremanje nije uspjelo.', 'Saving failed.'));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (entity: Editing['entity'], id: number) => {
        if (!window.confirm(t('Obrisati zapis?', 'Delete record?'))) return;
        try {
            await mutate(`/gtm-scrap/${entity}/${id}`, token, 'DELETE');
            setEditing(null);
            load();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : t('Brisanje nije uspjelo.', 'Delete failed.'));
        }
    };

    const saveSettings = async () => {
        if (!settingsForm) return;
        try {
            await mutate('/gtm-scrap/settings', token, 'PATCH', {
                deviation_threshold_pct: toNum(settingsForm.deviation_threshold_pct),
                price_outlier_pct: toNum(settingsForm.price_outlier_pct),
            });
            setSettingsForm(null);
            load();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : t('Spremanje nije uspjelo.', 'Saving failed.'));
        }
    };

    const exportXlsx = async () => {
        try {
            await downloadFile(`/gtm-scrap/export.xlsx${query()}`, token, `lom-gtm-${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch {
            setError(t('Izvoz nije uspio.', 'Export failed.'));
        }
    };

    const allNotes: Note[] = data ? [...data.batches.flatMap((b) => b.notes), ...data.unbatched_notes] : [];

    const th: React.CSSProperties = { textAlign: 'left', padding: '0.3rem 0.5rem', borderBottom: '1px solid #999', whiteSpace: 'nowrap' };
    const td: React.CSSProperties = { padding: '0.25rem 0.5rem', borderBottom: '1px solid #ddd', verticalAlign: 'top' };
    const num: React.CSSProperties = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };
    const thn: React.CSSProperties = { ...th, textAlign: 'right' };
    const flag: React.CSSProperties = { fontWeight: 700, textDecoration: 'underline' };

    const notesTable = (notes: Note[]) => (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
                <tr>
                    <th style={th}>{t('Ured', 'Office')}</th>
                    <th style={th}>{t('Otpremnica', 'Delivery note')}</th>
                    <th style={th}>{t('Datum', 'Date')}</th>
                    <th style={th}>{t('Poslano', 'Sent')}</th>
                    <th style={th}>{t('Stiglo', 'Arrived')}</th>
                    <th style={thn}>{t('Bruto g', 'Gross g')}</th>
                    <th style={thn}>{t('Fine Au g', 'Fine Au g')}</th>
                    <th style={th}>{t('Napomena', 'Note')}</th>
                    <th style={th} />
                </tr>
            </thead>
            <tbody>
                {notes.map((n) => (
                    <tr key={n.id}>
                        <td style={td}>{n.office}</td>
                        <td style={td}>
                            {n.note_no ?? ''}
                            {n.inbound_shipment_id ? <span className="muted"> ({t('iz dolazne pošiljke', 'from inbound')})</span> : null}
                        </td>
                        <td style={td}>{d(n.note_date)}</td>
                        <td style={td}>{d(n.sent_date)}</td>
                        <td style={td}>{d(n.arrived_date)}</td>
                        <td style={num}>{g(n.gross_g)}</td>
                        <td style={num}>{g(n.fine_au_g)}</td>
                        <td style={{ ...td, fontSize: '0.78rem' }}>{n.note ?? ''}</td>
                        <td style={td}>
                            <button type="button" className="secondary compact" onClick={() => setEditing({ entity: 'notes', id: n.id, values: toValues(NOTE_FIELDS, n) })}>
                                {t('Uredi', 'Edit')}
                            </button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    const invoicesTable = (invoices: Invoice[]) => (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginTop: '0.3rem' }}>
            <thead>
                <tr>
                    <th style={th}>{t('Račun', 'Invoice')}</th>
                    <th style={th}>{t('Datum', 'Date')}</th>
                    <th style={thn}>{t('Iznos', 'Amount')}</th>
                    <th style={th}>{t('Smjer', 'Direction')}</th>
                    <th style={thn}>{t('EUR po g fine', 'EUR per g fine')}</th>
                    <th style={thn}>{t('Od medijana', 'vs median')}</th>
                    <th style={th} />
                </tr>
            </thead>
            <tbody>
                {invoices.map((i) => (
                    <tr key={i.id}>
                        <td style={td}>{i.invoice_no ?? ''}</td>
                        <td style={td}>{d(i.invoice_date)}</td>
                        <td style={num}>{money(i.amount_eur)}</td>
                        <td style={td}>{i.direction}</td>
                        <td style={num} title={i.price_basis ? t(`osnovica: ${i.price_basis}`, `basis: ${i.price_basis}`) : undefined}>
                            {i.price_per_g === null ? '' : money(i.price_per_g)}
                        </td>
                        <td style={{ ...num, ...(i.price_outlier ? flag : {}) }}>
                            {pct(i.price_dev_pct)}
                            {i.price_outlier ? ` ${t('odstupa', 'outlier')}` : ''}
                        </td>
                        <td style={td}>
                            <button
                                type="button"
                                className="secondary compact"
                                onClick={() =>
                                    setEditing({
                                        entity: 'invoices',
                                        id: i.id,
                                        values: { ...toValues(INVOICE_FIELDS, i), batch_id: i.batch_id ? String(i.batch_id) : '', delivery_note_id: i.delivery_note_id ? String(i.delivery_note_id) : '' },
                                    })
                                }
                            >
                                {t('Uredi', 'Edit')}
                            </button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    return (
        <div className="module-shell">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem', flexWrap: 'wrap' }}>
                <div>
                    {onBack ? (
                        <button type="button" className="secondary compact" onClick={onBack}>
                            ← {t('Skladište', 'Warehouse')}
                        </button>
                    ) : null}
                    <p className="eyebrow">40.47</p>
                    <h2 style={{ margin: '0.2rem 0' }}>{t('Lom GTM', 'GTM scrap')}</h2>
                    <p className="muted" style={{ margin: 0 }}>
                        {t(
                            'Otpremnice ureda skupljaju se u pošiljku prema TCA. Odstupanje je fine TCA prema zbroju fine mase otpremnica, a cijena po gramu uspoređuje se s medijanom svih računa.',
                            'Office delivery notes are grouped into a TCA consignment. Deviation is TCA fine against the sum of delivery note fine; price per gram is compared with the median of all invoices.',
                        )}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button type="button" className="compact" onClick={() => setEditing({ entity: 'notes', id: null, values: { office: 'PLOVDIV' } })}>
                        {t('Nova otpremnica', 'New delivery note')}
                    </button>
                    <button type="button" className="compact" onClick={() => setEditing({ entity: 'batches', id: null, values: {}, noteIds: [] })}>
                        {t('Nova pošiljka TCA', 'New TCA consignment')}
                    </button>
                    <button type="button" className="compact" onClick={() => setEditing({ entity: 'invoices', id: null, values: { direction: 'ulazni' } })}>
                        {t('Novi račun', 'New invoice')}
                    </button>
                    <button type="button" className="secondary compact" onClick={load} disabled={loading}>
                        {t('Osvježi', 'Refresh')}
                    </button>
                    <button type="button" className="secondary compact" onClick={exportXlsx} disabled={!data}>
                        {t('Izvoz u Excel', 'Export to Excel')}
                    </button>
                </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 0.8rem', alignItems: 'center', marginTop: '0.8rem' }}>
                <select value={filters.office} onChange={(e) => setFilters((f) => ({ ...f, office: e.target.value }))} aria-label={t('Ured', 'Office')}>
                    <option value="">{t('Svi uredi', 'All offices')}</option>
                    <option value="PLOVDIV">Plovdiv</option>
                    <option value="SOFIJA">Sofija</option>
                </select>
                <label>
                    {t('Od', 'From')} <input type="date" value={filters.from} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
                </label>
                <label>
                    {t('Do', 'To')} <input type="date" value={filters.to} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} />
                </label>
                <select value={filters.state} onChange={(e) => setFilters((f) => ({ ...f, state: e.target.value }))} aria-label={t('Stanje', 'State')}>
                    <option value="">{t('Sva stanja', 'All states')}</option>
                    {(Object.keys(STATE_LABEL) as State[]).map((s) => (
                        <option key={s} value={s}>
                            {STATE_LABEL[s][locale]}
                        </option>
                    ))}
                </select>
            </div>

            {data ? (
                <div className="card" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 1.6rem', marginTop: '0.8rem', padding: '0.6rem 0.8rem' }}>
                    <span>
                        {t('Otpremnica', 'Delivery notes')} <strong>{data.totals.notes}</strong>
                    </span>
                    <span>
                        {t('Bruto', 'Gross')} <strong>{g(data.totals.gross_g)} g</strong>
                    </span>
                    <span>
                        {t('Fine Au', 'Fine Au')} <strong>{g(data.totals.fine_au_g)} g</strong>
                    </span>
                    <span>
                        {t('Fine AD', 'AD fine')} <strong>{g(data.totals.ad_fine_g)} g</strong>
                    </span>
                    <span>
                        {t('Fine TCA', 'TCA fine')} <strong>{g(data.totals.tca_fine_g)} g</strong>
                    </span>
                    <span>
                        {t('Odstupanje ukupno', 'Overall deviation')} <strong>{pct(data.totals.deviation_pct)}</strong>
                    </span>
                    <span>
                        {t('Računi', 'Invoices')} <strong>{money(data.totals.invoices_eur)}</strong>
                    </span>
                    <span className="muted">
                        {t('Medijan', 'Median')} {data.median_price_per_g === null ? '-' : money(data.median_price_per_g)} / g
                    </span>
                </div>
            ) : null}

            {error ? (
                <p role="alert" style={{ color: '#a00', marginTop: '0.8rem' }}>
                    {error}
                </p>
            ) : null}
            {loading && !data ? <p className="muted">{t('Učitavam...', 'Loading...')}</p> : null}

            {exceptions.length > 0 ? (
                <section className="card" style={{ marginTop: '1rem', padding: '0.6rem 0.8rem', border: '1px solid #999' }}>
                    <h3 style={{ margin: '0 0 0.4rem' }}>
                        {t('Iznimke', 'Exceptions')} ({exceptions.length})
                    </h3>
                    <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.88rem' }}>
                        {exceptions.map((e, i) => (
                            <li key={i}>
                                {e.label}: {e.ref ?? '-'} {e.date ? `(${d(e.date)})` : ''} {e.amount_eur !== undefined ? money(e.amount_eur) : ''}
                            </li>
                        ))}
                    </ul>
                </section>
            ) : null}

            {data ? (
                <>
                    {data.batches.map((b) => (
                        <section key={b.id} style={{ marginTop: '1.2rem', borderTop: '2px solid #666', paddingTop: '0.5rem' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem 1.2rem', alignItems: 'baseline' }}>
                                <h3 style={{ margin: 0 }}>
                                    {t('Pošiljka TCA', 'TCA consignment')} {b.ad_doc_no ?? `#${b.id}`}
                                </h3>
                                <span>{STATE_LABEL[b.state][locale]}</span>
                                {b.ad_doc_date ? <span>AD {d(b.ad_doc_date)}</span> : null}
                                {b.calc ? (
                                    <span>
                                        {t('Kalkulacija', 'Calculation')} {money(b.calc.iznos_eur)}, {BOOKING_LABEL[b.calc.booking_status] ?? b.calc.booking_status}
                                    </span>
                                ) : b.ad_doc_no ? (
                                    <span className="muted">{t('kalkulacija nije nađena', 'calculation not found')}</span>
                                ) : null}
                                {b.export_date ? (
                                    <span>
                                        {t('Izvoz', 'Export')} {d(b.export_date)}
                                    </span>
                                ) : null}
                                <span style={b.sent_diff_flag ? flag : undefined}>
                                    {t('Poslano', 'Sent')} {g(b.sent_g)} g{b.sent_diff_g !== null ? ` (${t('razlika', 'diff')} ${g(b.sent_diff_g)} g)` : ''}
                                </span>
                                <span>
                                    {t('Fine otpremnica', 'Notes fine')} {g(b.notes_fine_g)} g
                                </span>
                                <span>
                                    {t('Fine AD', 'AD fine')} {g(b.ad_fine_g)} g
                                </span>
                                <span>
                                    {t('Fine TCA', 'TCA fine')} {g(b.tca_fine_g)} g
                                </span>
                                <span style={b.deviation_flag ? flag : undefined}>
                                    {t('Odstupanje', 'Deviation')} {pct(b.deviation_pct)}
                                </span>
                                <span>
                                    {t('Računi', 'Invoices')} {money(b.invoices_eur)}
                                    {b.price_per_g !== null ? `, ${money(b.price_per_g)} / g` : ''}
                                </span>
                                <button
                                    type="button"
                                    className="secondary compact"
                                    onClick={() => setEditing({ entity: 'batches', id: b.id, values: toValues(BATCH_FIELDS, b), noteIds: b.notes.map((n) => n.id) })}
                                >
                                    {t('Uredi pošiljku', 'Edit consignment')}
                                </button>
                            </div>
                            {b.note ? (
                                <p className="muted" style={{ margin: '0.2rem 0', fontSize: '0.8rem' }}>
                                    {b.note}
                                </p>
                            ) : null}
                            {b.notes.length > 0 ? notesTable(b.notes) : <p className="muted">{t('Bez otpremnica.', 'No delivery notes.')}</p>}
                            {b.invoices.length > 0 ? invoicesTable(b.invoices) : null}
                        </section>
                    ))}

                    {data.unbatched_notes.length > 0 ? (
                        <section style={{ marginTop: '1.2rem', borderTop: '2px solid #666', paddingTop: '0.5rem' }}>
                            <h3 style={{ margin: '0 0 0.4rem' }}>
                                {t('Otpremnice bez pošiljke', 'Delivery notes without consignment')} ({data.unbatched_notes.length})
                            </h3>
                            {notesTable(data.unbatched_notes)}
                        </section>
                    ) : null}

                    {data.invoices_without_batch.length > 0 ? (
                        <section style={{ marginTop: '1.2rem', borderTop: '2px solid #666', paddingTop: '0.5rem' }}>
                            <h3 style={{ margin: '0 0 0.4rem' }}>
                                {t('Računi bez pošiljke', 'Invoices without consignment')} ({data.invoices_without_batch.length})
                            </h3>
                            {invoicesTable(data.invoices_without_batch)}
                        </section>
                    ) : null}

                    <section style={{ marginTop: '1.4rem', fontSize: '0.88rem' }}>
                        <h3 style={{ margin: '0 0 0.4rem' }}>{t('Postavke', 'Settings')}</h3>
                        {settingsForm ? (
                            <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                <label>
                                    {t('Granica odstupanja fine %', 'Fine deviation threshold %')}{' '}
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        style={{ width: '4rem' }}
                                        value={settingsForm.deviation_threshold_pct}
                                        onChange={(e) => setSettingsForm((s) => (s ? { ...s, deviation_threshold_pct: e.target.value } : s))}
                                    />
                                </label>
                                <label>
                                    {t('Granica cijene po gramu od medijana %', 'Price per gram vs median threshold %')}{' '}
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        style={{ width: '4rem' }}
                                        value={settingsForm.price_outlier_pct}
                                        onChange={(e) => setSettingsForm((s) => (s ? { ...s, price_outlier_pct: e.target.value } : s))}
                                    />
                                </label>
                                <button type="button" className="compact" onClick={saveSettings}>
                                    {t('Spremi', 'Save')}
                                </button>
                                <button type="button" className="secondary compact" onClick={() => setSettingsForm(null)}>
                                    {t('Odustani', 'Cancel')}
                                </button>
                            </div>
                        ) : (
                            <p style={{ margin: 0 }}>
                                {t('Odstupanje fine se označava iznad', 'Fine deviation flagged above')} {pct(data.settings.deviation_threshold_pct)},{' '}
                                {t('cijena po gramu iznad', 'price per gram above')} {pct(data.settings.price_outlier_pct)} {t('od medijana.', 'from median.')}{' '}
                                <button
                                    type="button"
                                    className="secondary compact"
                                    onClick={() =>
                                        setSettingsForm({ deviation_threshold_pct: String(data.settings.deviation_threshold_pct), price_outlier_pct: String(data.settings.price_outlier_pct) })
                                    }
                                >
                                    {t('Promijeni (superadministrator)', 'Change (superadministrator)')}
                                </button>
                            </p>
                        )}
                    </section>
                </>
            ) : null}

            {editing && data ? (
                <div
                    role="dialog"
                    aria-modal="true"
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflowY: 'auto', zIndex: 50, padding: '2rem 1rem' }}
                >
                    <div className="card" style={{ background: '#fff', width: 'min(760px, 100%)', padding: '1rem 1.2rem' }}>
                        <h3 style={{ margin: '0 0 0.6rem' }}>
                            {editing.entity === 'notes'
                                ? t('Otpremnica ureda', 'Office delivery note')
                                : editing.entity === 'batches'
                                  ? t('Pošiljka prema TCA', 'TCA consignment')
                                  : t('Račun', 'Invoice')}
                        </h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem 0.8rem' }}>
                            {fieldsFor(editing.entity).map((f) => (
                                <label key={f.key} style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem' }}>
                                    {t(f.hr, f.en)}
                                    {f.kind === 'select' ? (
                                        <select value={editing.values[f.key] ?? ''} onChange={(e) => setEditing((x) => (x ? { ...x, values: { ...x.values, [f.key]: e.target.value } } : x))}>
                                            {(f.options ?? []).map((o) => (
                                                <option key={o.value} value={o.value}>
                                                    {o.label}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            type={f.kind === 'date' ? 'date' : 'text'}
                                            inputMode={f.kind === 'num' ? 'decimal' : undefined}
                                            value={editing.values[f.key] ?? ''}
                                            onChange={(e) => setEditing((x) => (x ? { ...x, values: { ...x.values, [f.key]: e.target.value } } : x))}
                                        />
                                    )}
                                </label>
                            ))}
                            {editing.entity === 'invoices' ? (
                                <>
                                    <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem' }}>
                                        {t('Pošiljka', 'Consignment')}
                                        <select value={editing.values.batch_id ?? ''} onChange={(e) => setEditing((x) => (x ? { ...x, values: { ...x.values, batch_id: e.target.value } } : x))}>
                                            <option value="">{t('bez pošiljke', 'none')}</option>
                                            {data.batches.map((b) => (
                                                <option key={b.id} value={String(b.id)}>
                                                    {b.ad_doc_no ?? `#${b.id}`}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem' }}>
                                        {t('Otpremnica (osnovica za cijenu po gramu)', 'Delivery note (price basis)')}
                                        <select
                                            value={editing.values.delivery_note_id ?? ''}
                                            onChange={(e) => setEditing((x) => (x ? { ...x, values: { ...x.values, delivery_note_id: e.target.value } } : x))}
                                        >
                                            <option value="">{t('fine pošiljke', 'consignment fine')}</option>
                                            {allNotes.map((n) => (
                                                <option key={n.id} value={String(n.id)}>
                                                    {n.office} {n.note_no ?? ''}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                </>
                            ) : null}
                        </div>
                        {editing.entity === 'batches' ? (
                            <fieldset style={{ marginTop: '0.8rem', fontSize: '0.85rem' }}>
                                <legend>{t('Otpremnice u pošiljci', 'Delivery notes in consignment')}</legend>
                                {[...allNotes.filter((n) => editing.noteIds?.includes(n.id)), ...data.unbatched_notes.filter((n) => !editing.noteIds?.includes(n.id))].map((n) => (
                                    <label key={n.id} style={{ display: 'block' }}>
                                        <input
                                            type="checkbox"
                                            checked={editing.noteIds?.includes(n.id) ?? false}
                                            onChange={() =>
                                                setEditing((x) => {
                                                    if (!x) return x;
                                                    const ids = new Set(x.noteIds ?? []);
                                                    if (ids.has(n.id)) ids.delete(n.id);
                                                    else ids.add(n.id);
                                                    return { ...x, noteIds: Array.from(ids) };
                                                })
                                            }
                                        />{' '}
                                        {n.office} {n.note_no ?? ''} · {g(n.gross_g)} g · {t('fine', 'fine')} {g(n.fine_au_g)} g
                                    </label>
                                ))}
                            </fieldset>
                        ) : null}
                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                            <button type="button" className="compact" disabled={busy} onClick={save}>
                                {t('Spremi', 'Save')}
                            </button>
                            {editing.id !== null ? (
                                <button type="button" className="secondary compact" disabled={busy} onClick={() => remove(editing.entity, editing.id as number)}>
                                    {t('Obriši', 'Delete')}
                                </button>
                            ) : null}
                            <button type="button" className="secondary compact" onClick={() => setEditing(null)}>
                                {t('Odustani', 'Cancel')}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
